import React, {useEffect, useMemo, useRef, useState} from "react";
import {createRoot} from "react-dom/client";
import {Configure, InstantSearch} from "react-instantsearch-hooks-web";
import FilterContext from "./Context/FilterContext";
import SearchBar from "./Elements/SearchBar";
import HitsUpdater from "./Hits/HitsUpdater";
import Filters from "./Filters/Filters";
import nprogress from 'nprogress'
import 'nprogress/nprogress.css'
import cx from 'classnames'
import HitsList from "./Hits/HitsList";
import useProps from "./Hooks/useProps";
import MainMap from "./Map/MainMap";
import {join} from "lodash";
import {BrowserRouter as Router, useSearchParams} from 'react-router-dom'

// Main Search
// Route : search
// Controller : SearchController@search
// View : search.search

const aroundPrecision = 1000

const Main = props => {
    const { address, algolia, filters, mapbox: { token }, options } = props

    const [showFilters, setShowFilters] = useState(false);
    const [bbox, setBbox] = useState()
    const [radius, setRadius] = useState(options.radius !== '*' ? parseInt(options.radius) : undefined);
    const [speciality, setSpeciality] = useState(filters.speciality.length > 0 ? filters.speciality : '*');
    const [announceTypes, setAnnounceTypes] = useState(filters.announceTypes);
    const [aroundRadius, setAroundRadius] = useState(radius ? radius * aroundPrecision : undefined)
    const [loading, setLoading] = useState(true)
    const [zoom, setZoom] = useState(parseInt(options.zoom))
    const [lat, setLat] = useState(address.lat)
    const [lng, setLng] = useState(address.lng)
    const [updateFromGeocoder, setUpdateFromGeocoder] = useState(false)
    const [addressText, setAddressText] = useState(address.text)
    const [showRestartSearch, setShowRestartSearch] = useState(false)
    const [newLat, setNewLat] = useState(lat)
    const [newLng, setNewLng] = useState(lng)
    const [mapView, setMapView] = useState(true)
    const [queryFilters, setQueryFilters] = useState([])
    const [formattedFilters, setFormattedFilters] = useState('')
    const [searchParams, setSearchParams] = useSearchParams()
    const [searchAttributes, setSearchAttributes] = useState(filters.attributes)

    const reactiveContainer = useRef()
    const hitsContainer = useRef()

    const triggerNewPositionSearch = () => {
        setLoading(true)
        setNewLng(lng)
        setNewLat(lat)
    }

    const context = {
        showFilters, setShowFilters,
        speciality, setSpeciality,
        bbox, setBbox,
        radius, setRadius,
        loading, setLoading,
        zoom, setZoom,
        lat, setLat,
        lng, setLng,
        newLat, newLng,
        addressText, setAddressText,
        updateFromGeocoder, setUpdateFromGeocoder,
        showRestartSearch, setShowRestartSearch,
        mapView, setMapView,
        queryFilters, setQueryFilters,
        searchParams, setSearchParams,
        searchAttributes, setSearchAttributes,

        announceTypes,
        hitsContainer,
        triggerNewPositionSearch,
        token
    }

    const canSearchAroundPosition = useMemo(
        () => addressText.length > 0 && addressText !== 'France',
        [addressText]
    )

    useEffect(() => {
        setLoading(true)
        setAroundRadius(radius ? radius * aroundPrecision : undefined)
    }, [radius])

    useEffect(() => {
        setTimeout(() => {
            if (loading) setLoading(false)
        }, 500)
    }, [aroundRadius, loading])

    useEffect(() =>  {
        if (reactiveContainer.current === undefined) return;

        nprogress.configure({ parent: "#reactive-container" });
        loading ? nprogress.start() : nprogress.done()
    }, [reactiveContainer.current, loading])

    useEffect(() => {
        setFormattedFilters(join(queryFilters, ' AND '))
    }, [queryFilters])

    useEffect(() => {
        if (addressText.length === 0) return;

        setSearchParams(state => {
            setSearchAttributes(state => {
                state.density = []
                state.relief = []

                return state
            })

            state.delete('france')
            state.delete('mountain_or_sea[]')
            state.delete('town_tag[]')

            state.set('longitude', newLng)
            state.set('latitude', newLat)
            state.set('adresse', addressText)

            return state;
        }, { replace: true })
    }, [addressText, newLat, newLng])

    useEffect(() => {
        setSearchParams(state => {
            const key = 'specialite'

            speciality !== '*' ? state.set(key, speciality) : state.delete(key)

            return state;
        }, { replace: true })

    }, [speciality])

    return (
        <InstantSearch searchClient={algolia.client} indexName={algolia.prefix + 'announces'}>
            <Configure
                aroundRadius={canSearchAroundPosition ? aroundRadius : undefined}
                aroundPrecision={canSearchAroundPosition ? aroundPrecision : undefined}
                aroundLatLng={canSearchAroundPosition ? newLat + ',' + newLng : undefined}
                filters={formattedFilters}
            />

            <FilterContext.Provider value={context}>
                <HitsUpdater />
                <SearchBar />
                <Filters />
                <div id="reactive-container" ref={reactiveContainer} className={cx('relative flex bg-sky-50', {'md:h-full': mapView, 'min-h-screen': !mapView})}>
                    <div ref={hitsContainer} className={cx('flex flex-col gap-4 p-4', {'md:w-1/2 2xl:w-1/3 overflow-y-auto': mapView, 'flex-grow max-w-7xl mx-auto': !mapView})}>
                        <HitsList />
                    </div>

                    <div className={cx({'relative md:w-1/2 2xl:flex-grow hidden md:block flex-shrink-0': mapView, 'hidden': !mapView})}>
                        <MainMap {...{
                            token: token
                        }}/>
                    </div>

                    {/*{!canDisplayHits ? <AppPreloader /> : null}*/}
                </div>
            </FilterContext.Provider>
        </InstantSearch>
    );
};

function render() {
    const el = document.getElementById('instantsearch')

    const root = createRoot(el)

    const props = useProps({
        container: el
    })

    root.render(
        <Router>
            <Main {...props} />
        </Router>
    );
}

document.addEventListener('DOMContentLoaded', render)